import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getCardType, getCardGradient, formatCardNumber } from 'features/transaction-details/helpers';
import visaLogo from 'assets/visaLogo.png';
import masterLogo from 'assets/masterLogo.png';
import amexLogo from 'assets/amexLogo.png';
import ocaLogo from 'assets/ocaLogo.png';

interface CreditCardDisplayProps {
	cardNumber: string;
	cardholder: string;
	expiration: string;
	securityCode: string;
	cardStamp?: string;
	onCopyNumber: () => void;
	onCopyHolder: () => void;
	onCopyExpiration: () => void;
	onCopySecurityCode: () => void;
}

const cardLogos = {
	visa: visaLogo,
	mastercard: masterLogo,
	amex: amexLogo,
	oca: ocaLogo,
};

export const CreditCardDisplay = ({
	cardNumber,
	cardholder,
	expiration,
	securityCode,
	cardStamp,
	onCopyNumber,
	onCopyHolder,
	onCopyExpiration,
	onCopySecurityCode,
}: CreditCardDisplayProps): JSX.Element => {
	const cardType = cardStamp?.toLowerCase() || getCardType(cardNumber);
	const gradient = getCardGradient(cardType);
	const logo = cardLogos[cardType as keyof typeof cardLogos];

	return (
		<Box
			sx={{
				background: gradient,
				borderRadius: 2,
				padding: 2,
				color: 'white',
				position: 'relative',
				minHeight: 120,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
					borderRadius: 2,
					pointerEvents: 'none',
				},
			}}
		>
			{/* Logo de la tarjeta */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 1 }}>
				{logo && (
					<Box
						component="img"
						src={logo}
						alt={cardType}
						sx={{
							height: 30,
							width: 'auto',
							objectFit: 'contain',
							filter: 'brightness(0) invert(1)',
						}}
					/>
				)}
				<Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
					{cardType !== 'unknown' ? cardType : 'Tarjeta'}
				</Typography>
			</Box>

			{/* Número de tarjeta */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, zIndex: 1 }}>
				<Typography
					variant="h6"
					sx={{
						fontFamily: 'monospace',
						letterSpacing: 1.5,
						fontWeight: 500,
					}}
				>
					{formatCardNumber(cardNumber)}
				</Typography>
				<Tooltip title="Copiar número">
					<IconButton
						size="small"
						onClick={onCopyNumber}
						sx={{
							color: 'white',
							'&:hover': {
								background: 'rgba(255,255,255,0.2)',
							},
						}}
					>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Titular, expiración y CVV */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 1, gap: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box>
						<Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem' }}>
							Titular
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'uppercase' }}>
							{cardholder}
						</Typography>
					</Box>
					<Tooltip title="Copiar titular">
						<IconButton
							size="small"
							onClick={onCopyHolder}
							sx={{
								color: 'white',
								'&:hover': {
									background: 'rgba(255,255,255,0.2)',
								},
							}}
						>
							<ContentCopyIcon sx={{ fontSize: '0.8rem' }} />
						</IconButton>
					</Tooltip>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box sx={{ textAlign: 'right' }}>
						<Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem' }}>
							Vencimiento
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
							{expiration}
						</Typography>
					</Box>
					<Tooltip title="Copiar vencimiento">
						<IconButton
							size="small"
							onClick={onCopyExpiration}
							sx={{
								color: 'white',
								'&:hover': {
									background: 'rgba(255,255,255,0.2)',
								},
							}}
						>
							<ContentCopyIcon sx={{ fontSize: '0.8rem' }} />
						</IconButton>
					</Tooltip>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box sx={{ textAlign: 'right' }}>
						<Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', fontSize: '0.65rem' }}>
							CVV
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
							{securityCode}
						</Typography>
					</Box>
					<Tooltip title="Copiar CVV">
						<IconButton
							size="small"
							onClick={onCopySecurityCode}
							sx={{
								color: 'white',
								'&:hover': {
									background: 'rgba(255,255,255,0.2)',
								},
							}}
						>
							<ContentCopyIcon sx={{ fontSize: '0.8rem' }} />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
		</Box>
	);
};
